import type {
  RuntimeGraphqlExecuteRequest,
  RuntimeGraphqlExecuteResponse,
  RuntimeHealthResponse,
  RuntimeMongoExecuteRequest,
  RuntimeMongoExecuteResponse,
  RuntimePairCompleteRequest,
  RuntimePairCompleteResponse,
  RuntimePairStartResponse,
  RuntimeRestExecuteRequest,
  RuntimeRestExecuteResponse,
  RuntimeSqlExecuteRequest,
  RuntimeSqlExecuteResponse,
  RuntimeSchemaEntry,
  RuntimeWorkspaceStatusResponse,
  RuntimeWorkspaceSyncRequest,
} from "@spuerhund/shared/runtime";

const PAIRING_TOKEN_KEY = "spuerhund:runtime-pairing-token";

function resolveRuntimeBaseUrl(): string {
  // Env var override (build-time config)
  const envUrl = (import.meta.env.VITE_RUNTIME_BASE_URL as string | undefined)?.trim();
  if (envUrl) return envUrl;
  // Desktop app injects port via Electron main process
  const injectedPort = (window as unknown as Record<string, unknown>).__SPUERHUND_RUNTIME_PORT__;
  if (typeof injectedPort === "number") return `http://127.0.0.1:${injectedPort}`;
  // Default: CLI/brew service port
  return "http://127.0.0.1:4700";
}

// Lazy-resolved on first access (after Electron injects the port)
let _runtimeBaseUrl: string | null = null;
function getRuntimeBaseUrlInternal(): string {
  if (!_runtimeBaseUrl) _runtimeBaseUrl = resolveRuntimeBaseUrl();
  return _runtimeBaseUrl;
}

export function getRuntimeBaseUrl(): string {
  return getRuntimeBaseUrlInternal();
}

export function getRuntimeWebSocketUrl(): string {
  const baseUrl = new URL(getRuntimeBaseUrlInternal());
  baseUrl.protocol = baseUrl.protocol === "https:" ? "wss:" : "ws:";
  return baseUrl.toString();
}

export class RuntimeClientError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "RuntimeClientError";
    if (code !== undefined) {
      this.code = code;
    }
  }
}

function getPairingToken(): string | null {
  const token = localStorage.getItem(PAIRING_TOKEN_KEY)?.trim();
  return token || null;
}

export function hasRuntimePairingToken(): boolean {
  return getPairingToken() !== null;
}

export function clearRuntimePairingToken() {
  localStorage.removeItem(PAIRING_TOKEN_KEY);
}

function runtimeHeaders(withPairing = true): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (withPairing) {
    const token = getPairingToken();
    if (token) {
      headers["X-Spuerhund-Pairing-Token"] = token;
    }
  }
  return headers;
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let json: unknown = null;

  if (text.trim()) {
    try {
      json = JSON.parse(text);
    } catch {
      throw new RuntimeClientError(text || `Runtime returned HTTP ${response.status}`);
    }
  }

  if (!response.ok) {
    const errorBody = json as { error?: { message?: string; code?: string } } | null;
    if (response.status === 401) {
      clearRuntimePairingToken();
    }
    throw new RuntimeClientError(
      errorBody?.error?.message || `Runtime returned HTTP ${response.status}`,
      errorBody?.error?.code,
    );
  }

  return json as T;
}

async function runtimeFetch(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new RuntimeClientError(
      `Local Runtime is not reachable at ${getRuntimeBaseUrlInternal()}. Install the background service with \`spuerhund runtime install-service\` or start it with \`spuerhund runtime start\`. ${message}`,
      "RUNTIME_UNREACHABLE",
    );
  }
}

export async function getRuntimeHealth(): Promise<RuntimeHealthResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/health`, {
    method: "GET",
    headers: runtimeHeaders(),
  });
  return readJsonResponse<RuntimeHealthResponse>(response);
}

export async function startRuntimePairing(): Promise<RuntimePairStartResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/pair/start`, {
    method: "POST",
    headers: runtimeHeaders(false),
  });
  return readJsonResponse<RuntimePairStartResponse>(response);
}

export async function completeRuntimePairing(
  input: RuntimePairCompleteRequest,
): Promise<RuntimePairCompleteResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/pair/complete`, {
    method: "POST",
    headers: runtimeHeaders(false),
    body: JSON.stringify(input),
  });
  const result = await readJsonResponse<RuntimePairCompleteResponse>(response);
  localStorage.setItem(PAIRING_TOKEN_KEY, result.token);
  return result;
}

export async function startRuntimeLogin(): Promise<{ url: string }> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/auth/start`, {
    method: "POST",
    headers: runtimeHeaders(),
  });
  return readJsonResponse<{ url: string }>(response);
}

export async function getRuntimeAuthStatus(): Promise<RuntimeHealthResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/auth/status`, {
    method: "GET",
    headers: runtimeHeaders(),
  });
  return readJsonResponse<RuntimeHealthResponse>(response);
}

export async function executeRuntimeGraphql(
  input: RuntimeGraphqlExecuteRequest,
  signal?: AbortSignal,
): Promise<RuntimeGraphqlExecuteResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/execute/graphql`, {
    method: "POST",
    headers: runtimeHeaders(),
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
  return readJsonResponse<RuntimeGraphqlExecuteResponse>(response);
}

export async function executeRuntimeRest(
  input: RuntimeRestExecuteRequest,
  signal?: AbortSignal,
): Promise<RuntimeRestExecuteResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/execute/rest`, {
    method: "POST",
    headers: runtimeHeaders(),
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
  return readJsonResponse<RuntimeRestExecuteResponse>(response);
}

export async function executeRuntimeMongo(
  input: RuntimeMongoExecuteRequest,
  signal?: AbortSignal,
): Promise<RuntimeMongoExecuteResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/execute/mongo`, {
    method: "POST",
    headers: runtimeHeaders(),
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
  return readJsonResponse<RuntimeMongoExecuteResponse>(response);
}

export async function executeRuntimeSql(
  input: RuntimeSqlExecuteRequest,
  signal?: AbortSignal,
): Promise<RuntimeSqlExecuteResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/execute/sql`, {
    method: "POST",
    headers: runtimeHeaders(),
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
  return readJsonResponse<RuntimeSqlExecuteResponse>(response);
}

export async function getRuntimeWorkspaceStatus(): Promise<RuntimeWorkspaceStatusResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/workspace/status`, {
    method: "GET",
    headers: runtimeHeaders(false),
  });
  return readJsonResponse<RuntimeWorkspaceStatusResponse>(response);
}

export async function syncRuntimeWorkspaceSnapshot(
  snapshot: RuntimeWorkspaceSyncRequest["snapshot"],
): Promise<RuntimeWorkspaceStatusResponse> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/workspace/snapshot`, {
    method: "POST",
    headers: runtimeHeaders(),
    body: JSON.stringify({ snapshot } satisfies RuntimeWorkspaceSyncRequest),
  });
  return readJsonResponse<RuntimeWorkspaceStatusResponse>(response);
}

export async function refreshRuntimeCatalog(
  serverId: string,
): Promise<RuntimeSchemaEntry> {
  const response = await runtimeFetch(`${getRuntimeBaseUrlInternal()}/v1/catalog/servers/${encodeURIComponent(serverId)}/refresh`, {
    method: "POST",
    headers: runtimeHeaders(),
  });
  const payload = await readJsonResponse<{ schema: RuntimeSchemaEntry }>(response);
  return payload.schema;
}
